import type { TabConfig } from "@/components/shared/UniversalDetailPage/types/tab-types";
import type { Contracts } from "@/src/gql/graphql";

// ============================================================================
// CONTRACT DETAIL TABS CONFIGURATION
// ============================================================================
// Sales-oriented detail modal for the `contracts` entity. Overview is the
// default landing tab a salesperson sees first; Company / Contact / Deal are
// single-parent relationship tabs (Deal hidden when there is no deal_id);
// Communications reuses the company/contact-scoped activity tab.
//
// Each tab renders a component from ContractDetailTabComponents (see
// features/contracts/components/ContractDetailTabComponents.tsx).
// ============================================================================

export const contractDetailTabs: TabConfig<Contracts>[] = [
  {
    id: "overview",
    label: "Overview",
    icon: "FileText",
    variant: "blue",
    group: "core",
    priority: 1,
    content: {
      type: "custom",
      customComponent: "ContractOverviewDisplay",
    },
  },
  {
    // The raw agreement itself. Sits second — right after Overview — because
    // "what did we actually send them?" is the question reps open a contract to
    // answer, and it used to be the one thing Hub couldn't show.
    id: "document",
    label: "Document",
    icon: "FileText",
    variant: "orange",
    group: "core",
    priority: 2,
    content: {
      type: "custom",
      customComponent: "ContractDocumentDisplay",
    },
  },
  {
    id: "company",
    label: "Company",
    icon: "Building",
    variant: "red",
    group: "core",
    priority: 3,
    visible: (contract) =>
      typeof contract.company_id === "string" && contract.company_id.length > 0,
    content: {
      type: "custom",
      customComponent: "ContractCompanyDisplay",
    },
  },
  {
    id: "contact",
    label: "Contact",
    icon: "User",
    variant: "blue",
    group: "core",
    priority: 4,
    content: {
      type: "custom",
      customComponent: "ContractContactDisplay",
    },
  },
  {
    id: "deal",
    label: "Deal",
    icon: "Briefcase",
    variant: "green",
    group: "core",
    priority: 5,
    visible: (contract) =>
      typeof contract.deal_id === "string" && contract.deal_id.length > 0,
    content: {
      type: "custom",
      customComponent: "ContractDealDisplay",
    },
  },
  {
    id: "communications",
    label: "Communications",
    icon: "Mail",
    variant: "purple",
    group: "activity",
    priority: 1,
    visible: (contract) =>
      (typeof contract.company_id === "string" && contract.company_id.length > 0) ||
      (typeof contract.contact_id === "string" && contract.contact_id.length > 0),
    content: {
      type: "custom",
      customComponent: "ContractCommunicationsDisplay",
    },
  },
];

export const contractDetailDefaultTab = "overview";
